import React, { memo, useCallback, useMemo, useState } from 'react';
import Div from '@/shared/Div';
import VideoBanner, { BannerComponentType } from '@/shared/crisp/VideoBanner';
import SlickSlider from '@/shared/crisp/SlickSlider';
import is from '@/utils/is';
import SingleImagesBanner, { SingleImagesBannerProps } from '@/shared/crisp/SingleImagesBanner';
import withContentFlags from '@/hoc/withContentFlags';

interface BannerItem extends SingleImagesBannerProps {
  id: string;
  speed: number;
  type?: BannerComponentType;
  mobileMedia: SingleImagesBannerProps['mobileImage'];
  desktopMedia: SingleImagesBannerProps['desktopImage'];
}

interface HeroBannerCarouselProps {
  list: BannerItem[];
  clickable: boolean;
}

const HeroBannerCarousel = ({ list, clickable = false }: HeroBannerCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const renderBanner = useCallback((item: BannerItem) => {
    const { href, type, mobileMedia, desktopMedia } = item;

    if (href && type === BannerComponentType.Image) {
      return (
        <SingleImagesBanner
          mobileImage={mobileMedia}
          desktopImage={desktopMedia}
          href={href}
          title={''}
          subTitle={''}
          ctas={[]}
        />
      );
    }

    return <VideoBanner key={item.id} dataTestId={`banner-${item.id}`} {...item} />;
  }, []);

  const settings = useMemo(() => {
    const config: any = {
      autoplay: true,
      autoplaySpeed: list[currentIndex]?.speed,
      afterChange: (index: number): React.ReactNode => {
        setCurrentIndex(index);
        return null;
      },
      infinite: is.not.empty.array(list) && list.length > 1 ? true : false,
    };

    if (clickable) {
      config['appendDots'] = (dots: React.ReactNode) => {
        return (
          <Div
            style={{
              position: 'absolute',
              bottom: '48px',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            {dots}
          </Div>
        );
      };
    }

    return config;
  }, [list, clickable]);

  const slickSlider = (
    <SlickSlider settings={settings} className="main-slider" data={list} render={renderBanner} />
  );

  if (clickable) {
    return <Div className="relative"> {slickSlider}</Div>;
  }

  return (
    <Div dataTestId="hero-banner" className="bg-cm-primary pb-s8 md:pb-s9">
      {slickSlider}
    </Div>
  );
};

export default withContentFlags(memo(HeroBannerCarousel));
